import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/hooks/useAuth";
import { buildPostAuthPath, POST_AUTH_REDIRECT_KEY, sanitizeRedirect } from "@/lib/authRedirect";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";

const schema = z.object({
  email: z.string().trim().email("Invalid email").max(255),
  password: z.string().min(8, "Min 8 characters").max(72),
});

type AudienceProps = { defaultAudience?: "fan" | "athlete" };

const Auth = ({ defaultAudience }: AudienceProps = {}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialAudience: "fan" | "athlete" =
    defaultAudience ?? (searchParams.get("as") === "athlete" ? "athlete" : "fan");
  const [audience, setAudience] = useState<"fan" | "athlete">(initialAudience);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAthlete = audience === "athlete";
  const fallbackRedirect = isAthlete ? "/dashboard" : "/";
  const storedRedirect = useMemo(() => {
    if (typeof window === "undefined") return null;
    return sanitizeRedirect(window.localStorage.getItem(POST_AUTH_REDIRECT_KEY));
  }, []);
  const redirectTo =
    sanitizeRedirect(searchParams.get("next")) ??
    sanitizeRedirect((location.state as { from?: string } | null)?.from) ??
    storedRedirect ??
    fallbackRedirect;
  const authReturnPath = buildPostAuthPath(redirectTo, isAthlete);

  useEffect(() => {
    if (!loading && user && typeof window !== "undefined") {
      window.localStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-6 text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (user) return <Navigate to={redirectTo} replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}${authReturnPath}` },
        });
        if (error) throw error;
        toast.success("Account created! Check your email to confirm.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back");
        navigate(redirectTo);
      }
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setSubmitting(true);
    window.localStorage.setItem(POST_AUTH_REDIRECT_KEY, redirectTo);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${authReturnPath}`,
    });
    if (result.error) {
      setSubmitting(false);
      toast.error(result.error.message ?? "Google sign-in failed");
      return;
    }
    if (result.redirected) return;
    navigate(redirectTo);
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card rounded-[1.5rem] shadow-bold p-8">
        <Link to="/" className="flex justify-center mb-6">
          <Wordmark size="md" />
        </Link>

        {/* Audience toggle */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-muted mb-6">
          <button
            type="button"
            onClick={() => { setAudience("fan"); setMode("signin"); }}
            className={`py-2 rounded-full text-sm font-semibold transition-colors ${
              !isAthlete ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            Fan
          </button>
          <button
            type="button"
            onClick={() => { setAudience("athlete"); setMode("signin"); }}
            className={`py-2 rounded-full text-sm font-semibold transition-colors ${
              isAthlete ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            Athlete
          </button>
        </div>

        <h1 className="font-display text-3xl text-center">
          {isAthlete ? "Athlete login" : mode === "signin" ? "Sign in" : "Create account"}
        </h1>
        <p className="text-sm text-muted-foreground text-center mt-2">
          {isAthlete
            ? "Manage your profile, status, causes, and products."
            : "Save players you love and read the latest coverage."}
        </p>

        <button
          onClick={onGoogle}
          disabled={submitting}
          className="mt-6 w-full py-3 rounded-full bg-foreground text-background font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#fff" d="M21.35 11.1H12v3.2h5.35c-.23 1.4-1.65 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.05.78 3.75 1.45l2.55-2.45C16.6 3.95 14.5 3 12 3 6.98 3 3 7 3 12s3.98 9 9 9c5.2 0 8.65-3.65 8.65-8.8 0-.6-.07-1.05-.15-1.5z"/>
          </svg>
          Continue with Google
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
          <div className="h-px bg-border flex-1" />
          or
          <div className="h-px bg-border flex-1" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              className="w-full mt-1 px-4 py-3 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              maxLength={72}
              className="w-full mt-1 px-4 py-3 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
          >
            {submitting ? "..." : isAthlete ? "Sign in" : mode === "signin" ? "Sign in" : "Sign up"}
          </button>
        </form>

        {!isAthlete && mode === "signin" && (
          <Link
            to="/forgot-password"
            className="mt-3 block text-center text-sm text-muted-foreground hover:text-primary"
          >
            Forgot your password?
          </Link>
        )}

        {isAthlete ? (
          <Link
            to="/become-an-athlete"
            className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-primary"
          >
            Not an athlete yet? Apply here →
          </Link>
        ) : (
          <button
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="mt-4 w-full text-sm text-muted-foreground hover:text-primary"
          >
            {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
          </button>
        )}

        <Link to="/" className="block mt-6 text-center text-xs text-muted-foreground hover:text-primary">
          ← Back to site
        </Link>
      </div>
    </div>
  );
};

export default Auth;
